import { useLayoutEffect, useMemo, useReducer } from "react";
import { useTranslation } from "react-i18next";

function languageBase(lng) {
  return String(lng ?? "").split(/[-_]/)[0].toLowerCase();
}

function rtlFromLanguageCodes(lng) {
  const b = languageBase(lng);
  if (b === "ar" || b === "he" || b === "fa") return "rtl";
  return null;
}

function readEffectiveDir(i18n) {
  const lng = i18n.resolvedLanguage ?? i18n.language ?? "";
  const hard = rtlFromLanguageCodes(lng);
  if (hard) return hard;

  if (typeof document !== "undefined") {
    const fromDom = (
      document.documentElement.getAttribute("dir") ??
      document.documentElement.dir ??
      ""
    )
      .trim()
      .toLowerCase();
    if (fromDom === "rtl" || fromDom === "ltr") return fromDom;
  }

  if (typeof i18n.dir === "function") {
    const resolvedLng = lng || i18n.language || "en";
    const d = String(i18n.dir(resolvedLng)).trim().toLowerCase();
    if (d === "rtl") return "rtl";
  }
  return "ltr";
}

/** Aligns shell with `<html dir>`, `#root dir`, `languageChanged`, and Arabic base language codes. */
export function useEffectiveDocumentDir() {
  const { i18n } = useTranslation();
  const [tick, bump] = useReducer((x) => x + 1, 0);

  useLayoutEffect(() => {
    const pulse = () => bump();
    const onLang = () => {
      pulse();
      requestAnimationFrame(pulse);
    };
    i18n.on("languageChanged", onLang);
    i18n.on("initialized", onLang);

    const el = document.documentElement;
    const mo = new MutationObserver(pulse);
    mo.observe(el, { attributes: true, attributeFilter: ["dir", "lang"] });

    const root = document.getElementById("root");
    let rootMo = null;
    if (root) {
      rootMo = new MutationObserver(pulse);
      rootMo.observe(root, { attributes: true, attributeFilter: ["dir"] });
    }

    pulse();
    return () => {
      i18n.off("languageChanged", onLang);
      i18n.off("initialized", onLang);
      mo.disconnect();
      rootMo?.disconnect();
    };
  }, [i18n]);

  return useMemo(
    () => readEffectiveDir(i18n),
    [i18n.language, i18n.resolvedLanguage, tick],
  );
}
